import { type MeResponse, type PermissionCode, isAdminRole, roleHasGmsAccess } from "@seagames/contracts";
import { groupGrants } from "../travel/access";

/**
 * Who sees what on the Transportation pages (M10, PLAN §3.4 / §4). NONE of
 * this decides anything: every read is scoped in SQL (`orgScopeWith`,
 * `providerOwns`, `cardReadableWith`) and every write is re-checked by the
 * API. It only picks which page, tabs and buttons to draw, so nobody clicks
 * into a page of 403s.
 *
 * Who someone IS comes from the active role, never from a permission (bug 7):
 * a transport company login is TRANSPORT_PROVIDER whatever its group holds,
 * and it gets the provider view, not the dispatch desk. A group's permission
 * counts only while the active role is the group's and the group is active
 * (`groupGrants` → `roleHasGmsAccess`).
 */

type Session = Pick<MeResponse, "active_role" | "group">;

/** The permissions that open the Transportation page for a group (nav + route guard). */
export const TRANSPORT_PAGE_PERMISSIONS = ["transport.dispatch", "transport.request"] as const satisfies readonly PermissionCode[];

/** A transport company's login, with an active group for that role (X17). */
export const isProvider = (me: Session): boolean => me.active_role === "TRANSPORT_PROVIDER" && roleHasGmsAccess(me.active_role, me.group);

/** The dispatch desk: suggestions, transfers, allocation, auto-assign, cancel / reject any trip. Admins or a non-provider group with `transport.dispatch`. */
export const canDispatch = (me: Session): boolean =>
	isAdminRole(me.active_role) || (me.active_role !== "TRANSPORT_PROVIDER" && groupGrants(me, "transport.dispatch"));

/** May ask for a venue trip ("Request a trip"): the dispatch desk, or a group with `transport.request` (NOC or host). */
export const canRequestTrips = (me: Session): boolean => canDispatch(me) || (me.active_role !== "TRANSPORT_PROVIDER" && groupGrants(me, "transport.request"));

/**
 * Start / Arrived on the trips of its own vehicles: a provider login whose
 * group holds `transport.dispatch` (reads need no permission; pressing does —
 * `providerOwns` checks the permission in the same group). Never makes it a
 * desk dispatcher (`canDispatch` stays false for it).
 */
export const providerCanStep = (me: Session): boolean => isProvider(me) && groupGrants(me, "transport.dispatch");

/** The page at all: admins, a dispatch / request group, or a provider. */
export const canSeeTransport = (me: Session): boolean => canDispatch(me) || canRequestTrips(me) || isProvider(me);

/** The Fleet tab: the dispatch desk (writes) or a provider (its own fleet, read-only). */
export const canSeeFleet = (me: Session): boolean => canDispatch(me) || isProvider(me);

/** Fleet, clusters, places, drive minutes, drop-offs, settings: admins and the dispatch desk write; a provider only reads. */
export const canWriteFleet = (me: Session): boolean => canDispatch(me);

/** Transport providers and their groups' facility rows (SUPER_ADMIN only). */
export const canManageProviders = (me: Session): boolean => me.active_role === "SUPER_ADMIN";

/**
 * Which Transportation page a session gets at `/logistics/transportation`:
 *   dispatch   Overview · Requests · Fleet (admins, `transport.dispatch`)
 *   provider   trips on its vehicles + its fleet, read-only
 *   noc        "Your trips" + Request a trip (`transport.request`, no dispatch)
 *   null       none — the guard sends it home
 */
export type TransportView = "dispatch" | "provider" | "noc";

export function transportView(me: Session): TransportView | null {
	if (canDispatch(me)) return "dispatch";
	if (isProvider(me)) return "provider";
	if (canRequestTrips(me)) return "noc";
	return null;
}
